import { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { Tree } from 'primereact/tree'
import { ResourcesService } from '@/app/services/ResourcesService'
import { Color, LuxDialog, LuxIconSpinner } from '@lux/wardrobe-lib'
import { FolderType, ResourceProps, TreeFolderProps } from '@/app/models/new-resource'
import './DialogSelectRecursiveFolder.scss'

const DialogSelectRecursiveFolder = (props: {
    title: string
    visible: boolean
    loading: boolean
    description: string
    folders: ResourceProps[]
    selectedFolderId: string
    selectedFolderObject: ResourceProps
    onSave: () => void
    onHide: () => void
    loadMoreFolders: (treeFolder: TreeFolderProps) => void
    setSelectedFolderId: (selectedFolderId: string) => void
    setSelectedFolderObject: (obj: { _id: string; title: string; folderType: FolderType }) => void
}) => {
    const {
        title,
        folders,
        visible,
        loading,
        description,
        selectedFolderId,
        selectedFolderObject,
        onSave,
        onHide,
        loadMoreFolders,
        setSelectedFolderId,
        setSelectedFolderObject,
    } = props

    const intl = useIntl()

    const componentClass = 'smart-resources-dialog-select-recursive-folder'

    const [nodes, setNodes] = useState([])
    const [loadNodes, setLoadNodes] = useState(false)
    const [selectedKey, setSelectedKey] = useState<string>(null)

    const selectNode = (node: any) => {
        setSelectedKey(node.key + '')
        setSelectedFolderId(node._id)
        setSelectedFolderObject({ _id: node._id, title: node.label, folderType: node.folderType })
    }

    const deepSplice = (_nodes, indices, deleteCount, ...toBeInserted) => {
        const last = indices.pop()
        const finalItems = indices.reduce((acc, i) => acc[i].children, _nodes)
        finalItems.splice(last, deleteCount, ...toBeInserted)
        setNodes(_nodes)
    }

    const loadOnExpand = (event) => {
        setLoadNodes(true)
        let node = { ...event.node }
        node.children = []
        ResourcesService.GetResources({ folderId: node._id })
            .then((res: any) => {
                if (res && res.data && res.data.data) {
                    node.children = res.data.data
                        .filter((f) => f._id !== node._id)
                        .map((child: ResourceProps, i: number) => {
                            return {
                                _id: child._id,
                                label: child.title,
                                key: `${event.node.key}-${i}`,
                                leaf: child.countSubFolders === 0,
                                folderType: child.folderType
                            }
                        })
                    let nodeKey = event.node.key + ''
                    let keys = nodeKey.split('-')
                    let _nodes = [...nodes]
                    deepSplice(_nodes, keys, 1, node)
                    setLoadNodes(false)
                }
            })
            .catch((error: Error) => {
                setLoadNodes(false)
            })
    }

    useEffect(() => {
        if (folders) {
            setNodes(
                folders.map((folder: ResourceProps, i: number) => {
                    return {
                        key: i + '',
                        _id: folder._id,
                        label: folder.title,
                        leaf: folder.countSubFolders === 0,
                        folderType: folder.folderType,
                    }
                })
            )
        }
    }, [folders])

    useEffect(() => {
        if (visible && selectedFolderObject._id !== '') {
            let key = null
            let tmpNodes = [...nodes]
            for (let i = 0; i < tmpNodes.length; i++) {
                let tmpNode = tmpNodes[i]
                if (tmpNode._id === selectedFolderObject._id) {
                    key = tmpNode.key
                }
            }
            setSelectedKey(key + '')
        }
    }, [visible, selectedFolderObject])

    return (
        <div className={componentClass}>
            <LuxDialog
                onSave={onSave}
                onHide={() => {
                    onHide()
                    if (selectedFolderObject._id === '') {
                        setSelectedKey(null)
                    }
                }}
                title={title}
                visible={visible}
                customActions={true}
                description={description}
                maskClassName={`${componentClass}__mask`}
                cancelLabel={intl.formatMessage({
                    id: 'modals.dialog.buttons.back',
                    defaultMessage: 'Back',
                })}
                confirmLabel={intl.formatMessage({
                    id: 'modals.dialog.buttons.save',
                    defaultMessage: 'Save',
                })}
                actions={[
                    {
                        color: Color.BLACK,
                        variant: 'outlined',
                        onClick: () => {
                            onHide()
                            if (selectedFolderObject._id === '') {
                                setSelectedKey(null)
                            }
                        },
                        text: intl.formatMessage({
                            id: 'modals.dialog.buttons.back',
                            defaultMessage: 'Back',
                        }),
                    },
                    {
                        color: Color.WARNING,
                        onClick: () => onSave(),
                        disabled: selectedFolderId === '' || !selectedFolderId,
                        text: intl.formatMessage({
                            id: 'modals.dialog.buttons.save',
                            defaultMessage: 'Save',
                        }),
                    },
                ]}>
                {loading ? (
                    <div className={`${componentClass}__tree__loading`}>
                        <LuxIconSpinner />
                    </div>
                ) : (
                    <div className={`${componentClass}__tree__new`}>
                        <Tree
                            value={nodes}
                            loading={loadNodes}
                            selectionMode="single"
                            onExpand={loadOnExpand}
                            selectionKeys={selectedKey}
                            onNodeClick={(e: any) => selectNode(e.node)}
                        />
                    </div>
                )}
            </LuxDialog>
        </div>
    )
}

export default DialogSelectRecursiveFolder
